<script setup>
import { computed, reactive, watch } from 'vue'
import { useStore } from 'vuex'
import DikeSectionIndexTitle from '@/components/dikeSection/DikeSectionIndexTitle.vue'
import DikeProfileIndexTitle from '@/components/dikeProfile/DikeProfileIndexTitle.vue'
import SchematisationTable from '@/components/schematisation/SchematisationTable.vue'

const props = defineProps({
  hideUnimportantItems: {
    type: Boolean,
    default: false
  }
})

const store = useStore()

const expandedSections = reactive({})
const expandedProfiles = reactive({})
const loadingSections = reactive({})
const loadingProfiles = reactive({})
const profilesCache = reactive({})
const schematisationsCache = reactive({})

const dikeSections = computed(() => store.state.dikeSection.items)
const dikeProfiles = computed(() => store.state.dikeProfiles.items)
const stabSchematisations = computed(() => store.state.stabSchematisation.items)

const updateSchematisationsCache = (schematisations) => {
  Object.keys(schematisationsCache).forEach((profileId) => {
    schematisationsCache[profileId] = []
  })

  if (schematisations && Array.isArray(schematisations)) {
    const profileSchematisationsMap = {}

    schematisations.forEach((schematisation) => {
      Object.values(profilesCache)
        .flat()
        .forEach((profile) => {
          if (profile && schematisation.dike_profile === profile.properties.geometry_id) {
            if (!profileSchematisationsMap[profile.id]) {
              profileSchematisationsMap[profile.id] = []
            }
            profileSchematisationsMap[profile.id].push(schematisation)
          }
        })
    })
    Object.keys(profileSchematisationsMap).forEach((profileId) => {
      schematisationsCache[profileId] = profileSchematisationsMap[profileId]
    })
  }
}

const onSectionToggle = (sectionId, isExpanding) => {
  expandedSections[sectionId] = isExpanding
  if (isExpanding && !profilesCache[sectionId]) {
    loadProfilesForSection(sectionId)
  }
}

const onProfileToggle = (profileId, isExpanding) => {
  expandedProfiles[profileId] = isExpanding
  if (isExpanding && !schematisationsCache[profileId]) {
    loadSchematisationsForProfile(profileId)
  }
}

const shouldLoadProfiles = (sectionId) => {
  return expandedSections[sectionId]
}

const isLoadingProfiles = (sectionId) => {
  return loadingSections[sectionId]
}

const shouldLoadSchematisations = (profileId) => {
  return expandedProfiles[profileId]
}

const isLoadingSchematisations = (profileId) => {
  return loadingProfiles[profileId]
}

const loadProfilesForSection = async (sectionId) => {
  if (profilesCache[sectionId] || loadingSections[sectionId]) {
    return
  }
  loadingSections[sectionId] = true

  await new Promise((resolve) => setTimeout(resolve, 10))

  const section = dikeSections.value.features.find((s) => s.id === sectionId)
  if (section) {
    const profiles =
      dikeProfiles.value?.features?.filter(
        (dikeProfile) => section.properties.geometry_id === dikeProfile.properties.dikesection_id
      ) || []
    profilesCache[sectionId] = profiles

    updateSchematisationsForProfiles(profiles)
  }

  loadingSections[sectionId] = false
}

const loadSchematisationsForProfile = async (profileId) => {
  if (schematisationsCache[profileId] || loadingProfiles[profileId]) {
    return
  }

  loadingProfiles[profileId] = true

  await new Promise((resolve) => setTimeout(resolve, 10))

  const profile = Object.values(profilesCache)
    .flat()
    .find((p) => p.id === profileId)

  if (profile) {
    const schematisations =
      stabSchematisations.value?.filter(
        (stabSchematisation) => stabSchematisation.dike_profile === profile.properties.geometry_id
      ) || []

    schematisationsCache[profileId] = schematisations
  }

  loadingProfiles[profileId] = false
}

const updateSchematisationsForProfiles = (profiles) => {
  if (!stabSchematisations.value || !Array.isArray(stabSchematisations.value)) {
    return
  }

  profiles.forEach((profile) => {
    if (!schematisationsCache[profile.id]) {
      const schematisations = stabSchematisations.value.filter(
        (stabSchematisation) => stabSchematisation.dike_profile === profile.properties.geometry_id
      )
      schematisationsCache[profile.id] = schematisations
    }
  })
}

const getCachedProfiles = (dikeSection) => {
  return profilesCache[dikeSection.id] || []
}

const getCachedSchematisations = (dikeProfile) => {
  return schematisationsCache[dikeProfile.id] || []
}

const dikeProfileSchematisationCount = (dikeProfile) => {
  if (!dikeProfile || !stabSchematisations.value) {
    return 0
  }

  return stabSchematisations.value.filter(
    (schematisation) => schematisation.dike_profile === dikeProfile.properties.geometry_id
  ).length
}

const dikeSectionSchematisationCount = (dikeSection) => {
  if (!dikeSection || !stabSchematisations.value || !dikeProfiles.value) {
    return 0
  }

  const sectionProfiles =
    dikeProfiles.value?.features?.filter(
      (dikeProfile) => dikeSection.properties.geometry_id === dikeProfile.properties.dikesection_id
    ) || []

  return stabSchematisations.value.filter((schematisation) => {
    return sectionProfiles.some(
      (profile) => profile.properties.geometry_id === schematisation.dike_profile
    )
  }).length
}

watch(
  stabSchematisations,
  (newSchematisations) => {
    updateSchematisationsCache(newSchematisations)
  },
  { deep: true, immediate: true }
)
</script>

<template>
  <v-list density="compact" v-if="dikeSections">
    <template v-for="dikeSection in dikeSections.features" :key="dikeSection.id">
      <v-list-group>
        <template v-slot:activator="{ props, isOpen }">
          <v-list-item
            class="pl-2"
            v-bind="props"
            density="compact"
            @click="onSectionToggle(dikeSection.id, !isOpen)"
          >
            <DikeSectionIndexTitle
              :item="dikeSection"
              :number-of-schematisation="dikeSectionSchematisationCount(dikeSection)"
            />
          </v-list-item>
        </template>

        <template v-if="shouldLoadProfiles(dikeSection.id)">
          <div v-if="isLoadingProfiles(dikeSection.id)" class="pa-2">
            <v-skeleton-loader v-for="n in 0" :key="n" type="list-item" class="mb-1" />
          </div>
          <template v-else>
            <template v-for="dikeProfile in getCachedProfiles(dikeSection)" :key="dikeProfile.id">
              <v-list-group sub-group>
                <template v-slot:activator="{ isOpen, props }">
                  <v-list-item
                    class="profile-padding"
                    v-bind="props"
                    density="compact"
                    @click="onProfileToggle(dikeProfile.id, !isOpen)"
                  >
                    <DikeProfileIndexTitle
                      v-if="dikeProfile"
                      :active="isOpen"
                      :item="dikeProfile"
                      :number-of-schematisation="dikeProfileSchematisationCount(dikeProfile)"
                    />
                  </v-list-item>
                </template>

                <template v-if="shouldLoadSchematisations(dikeProfile.id)">
                  <div v-if="isLoadingSchematisations(dikeProfile.id)" class="pa-2">
                    <v-skeleton-loader
                      v-for="n in 0"
                      :key="n"
                      type="list-item-two-line"
                      class="mb-1"
                    />
                  </div>
                  <SchematisationTable
                    v-else
                    :hide-unimportant-items="true"
                    :items="getCachedSchematisations(dikeProfile)"
                  />
                </template>
              </v-list-group>
            </template>
          </template>
        </template>
      </v-list-group>
    </template>
  </v-list>
</template>
